/**
 * DentalDiaryCard — дентален дневник с отметки и серия дни.
 * Данни: GET /api/dental-intelligence/diary · POST /diary/checkin
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Circle, Flame, ListChecks } from "lucide-react";
import { useAuth } from "@/App";

const DAY_SHORT = ["нд", "пн", "вт", "ср", "чт", "пт", "сб"];

export default function DentalDiaryCard({ isDark = false, cardCls = "", childId = null }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/diary`, {
        params: childId ? { child_id: childId } : {},
      });
      setD(data);
    } catch { setD(null); }
  }, [API, childId]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";

  const toggle = async (key, done) => {
    try {
      const { data } = await axios.post(`${API}/dental-intelligence/diary/checkin`, {
        date: d.today, habit_key: key, done, child_id: childId,
      });
      setD(data);
      // iter1110 — notify DHI page so caries/microbiome/ecosystem scores
      // reload immediately rather than waiting for a manual refresh.
      try {
        window.dispatchEvent(new CustomEvent("nutramilo:dental:updated"));
      } catch { /* no-op */ }
    } catch { toast.error("Отметката не се запази"); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-diary-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-cyan-500" /> Дентален дневник
          </div>
          <p className="text-[11px] opacity-75 mt-0.5" data-testid="dhi-diary-today">
            {d.today_bg}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1
            ${d.streak > 0 ? "bg-orange-500/20 text-orange-600" : "bg-slate-500/15 opacity-70"}`}
            data-testid="dhi-diary-streak">
            <Flame className="w-3.5 h-3.5" /> {d.streak} дни
          </span>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600"
            data-testid="dhi-diary-week-percent">
            {d.week_progress.percent}% за 7 дни
          </span>
        </div>
      </div>

      {/* последните 7 дни */}
      <div className="flex gap-1 mt-3" data-testid="dhi-diary-week">
        {d.week.map((r) => {
          const dt = new Date(r.date);
          return (
            <div key={r.date} className="flex-1 text-center" data-testid={`dhi-diary-day-${r.date}`}>
              <div className="h-12 rounded-lg overflow-hidden flex items-end"
                style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)" }}>
                <div className="w-full bg-gradient-to-t from-cyan-600 to-sky-400"
                  style={{ height: `${r.percent}%` }} />
              </div>
              <div className="text-[9px] opacity-60 mt-0.5">{DAY_SHORT[dt.getDay()]}</div>
            </div>
          );
        })}
      </div>

      {/* навиците за днес */}
      <div className="mt-3 space-y-1.5" data-testid="dhi-diary-habits">
        {d.habits.map((h) => (
          <div key={h.key} className={`p-2 rounded-lg border ${border} flex items-start gap-2`}
            data-testid={`dhi-diary-habit-${h.key}`}>
            <button onClick={() => toggle(h.key, !h.done_today)} className="mt-0.5"
              data-testid={`dhi-diary-toggle-${h.key}`}>
              {h.done_today
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                : <Circle className="w-4 h-4 opacity-50" />}
            </button>
            <div className="min-w-0">
              <div className={`text-[11px] font-bold ${h.done_today ? "line-through opacity-60" : ""}`}>
                {h.title_bg}
                {h.days_done_7 > 0 && (
                  <span className="ml-1 text-[10px] font-normal opacity-60">
                    · {h.days_done_7}/7 дни
                  </span>
                )}
              </div>
              <div className="text-[10px] opacity-70">{h.how_bg}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
